import { useFormContext } from 'react-hook-form';
import { Bell } from 'lucide-react';
import { useAuthStore } from '@/auth/auth-store';
import { useReminderProfilesList } from '@/services/scheduling/use-reminder-profiles-list';
import {
  type ReminderProfile,
  reminderRules,
} from '@/services/scheduling/use-prebook-appointment-profile';
import type { AppointmentFormValues } from '@/types/appointment-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface ReminderPickerViewProps {
  /** Closes the view without persisting changes. */
  onCancel: () => void;
}

/**
 * ReminderPickerView — full panel-content replacement.
 * Mirrors Flutter `reminder_profile_selection_widget.dart`:
 *   - Top bar: Back / Reminders / (no Save — selection auto-commits)
 *   - List of practice reminder profiles (name + per-rule types + when-to-send)
 *   - Each row has a Select button — tap → write to form, close
 *
 * The default reminder is auto-populated by
 * `PreBookSelectedAppointmentProfile.reminderList.firstWhere(enableInAppBooking)`
 * in `appointment-profile-section.tsx`. This picker is the manual override.
 */
export function ReminderPickerView({ onCancel }: ReminderPickerViewProps) {
  const { setValue } = useFormContext<AppointmentFormValues>();
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? null;

  const { data: profiles = [], isLoading } = useReminderProfilesList(practiceId);

  const select = (p: ReminderProfile) => {
    if (!p.id) return;
    setValue('reminderProfileId', p.id, { shouldValidate: true });
    setValue('reminderProfileName', p.profileName ?? null);
    setValue('reminderProfileRules', reminderRules(p));
    setValue('reminderProfile', p);
    onCancel();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="booking-subhead-title">Reminder</h2>
        <span className="w-10" aria-hidden />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 rounded-lg bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : profiles.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8 leading-snug">
              No reminder profiles configured for this practice.
            </p>
          ) : (
            profiles.map((p, i) => {
              const types = (p.reminderProfileType ?? [])
                .map((t) => t.type ?? 'n/a')
                .join(' • ');
              const rules = reminderRules(p).join(' • ');
              return (
                <div
                  key={p.id || `reminder-${i}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border-default bg-surface-card"
                >
                  <Bell className="size-5 text-text-secondary flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate leading-snug">
                      {p.profileName ?? '—'}
                    </p>
                    {types ? (
                      <p className="text-xs text-text-muted truncate leading-snug">
                        {types}
                      </p>
                    ) : null}
                    {rules ? (
                      <p className="text-xs text-text-muted leading-snug">{rules}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => select(p)}
                    className="h-8 px-3 rounded-md bg-grey-100 hover:bg-blue-100 text-sm font-semibold text-brand-primary flex-shrink-0"
                  >
                    Select
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
